<script setup>
import { computed, ref } from 'vue';
import { usePipelineBoard } from 'dashboard/composables/usePipelineBoard';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanColumn from '../components/KanbanColumn.vue';
import AddCardDialog from '../components/AddCardDialog.vue';

const {
  pipelines,
  activePipeline,
  activePipelineId,
  columns,
  isLoading,
  switchPipeline,
  onColumnChange,
  createCard,
} = usePipelineBoard();

const addCardDialogRef = ref(null);
const isCreatingCard = ref(false);

const stages = computed(() => columns.value.map(column => column.stage));

const openAddCard = (stageId = null) => {
  addCardDialogRef.value?.open(stageId);
};

const onCreateCard = async attributes => {
  isCreatingCard.value = true;
  try {
    const created = await createCard(attributes);
    if (created) addCardDialogRef.value?.onSuccess();
  } finally {
    isCreatingCard.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between flex-shrink-0 gap-4 px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-kanban size-5 text-n-slate-11" />
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ $t('PIPELINE.HEADER') }}
        </h1>
      </div>
      <div class="flex items-center gap-3">
        <select
          v-if="pipelines.length > 1"
          :value="activePipelineId"
          class="px-3 py-1.5 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
          @change="switchPipeline(Number($event.target.value))"
        >
          <option
            v-for="pipeline in pipelines"
            :key="pipeline.id"
            :value="pipeline.id"
          >
            {{ pipeline.name }}
          </option>
        </select>
        <Button
          v-if="activePipeline"
          :label="$t('PIPELINE.ADD_CARD.BUTTON')"
          color="blue"
          @click="openAddCard()"
        />
      </div>
    </header>

    <div
      v-if="isLoading"
      class="flex items-center justify-center flex-1 gap-2 text-n-slate-11"
    >
      <Spinner class="size-5" />
      {{ $t('PIPELINE.LOADING') }}
    </div>

    <div
      v-else-if="!activePipeline"
      class="flex flex-col items-center justify-center flex-1 gap-2 text-n-slate-11"
    >
      <span class="i-lucide-kanban size-8" />
      {{ $t('PIPELINE.EMPTY_STATE') }}
    </div>

    <main v-else class="flex flex-1 gap-4 p-6 overflow-x-auto">
      <KanbanColumn
        v-for="column in columns"
        :key="column.stage.id"
        :stage="column.stage"
        :items="column.items"
        @change="onColumnChange"
        @add="openAddCard"
      />
    </main>

    <AddCardDialog
      ref="addCardDialogRef"
      :stages="stages"
      :is-loading="isCreatingCard"
      @create="onCreateCard"
    />
  </div>
</template>
